import { call, delay, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

// import { preferencesV2Actions, preferencesV2Types } from 'app-components/flux/preferences-v2';
import { preferencesActions } from 'app-components/flux/preferences';

import { getEnvironmentConfig, getHostConfig, getLogger, httpClientInterface, tracker } from '../../utils/core';
import store from '../../utils/redux-store';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { datasetsActions } from '../datasets';

import * as actions from './authActions';
import * as authSelectors from './authSelectors';
import * as authTypes from './authTypes';

const log = getLogger('flux/auth/sagas');

// if multiple exchange refresh token requests triggered simultaneously then we
// - execute only the first one (exchangeRefreshTokenInProgress)
// - queue the rest of them (exchangeRefreshTokenWaitList)
// - once exchange complete we resolve/reject promises for queued actions
let exchangeRefreshTokenInProgress = false;
const exchangeRefreshTokenWaitList = [];
export function* exchangeRefreshTokenActionWatcher() {
  yield takeEvery(actions.exchangeRefreshToken, function* exchangeRefreshToken(action) {
    if (exchangeRefreshTokenInProgress) {
      exchangeRefreshTokenWaitList.push(action);
    } else {
      let useRefreshTokenAction;
      if (action.payload) {
        useRefreshTokenAction = action;
      } else {
        const refreshToken = yield select(authSelectors.getRefreshToken);
        if (refreshToken) {
          useRefreshTokenAction = actions.exchangeRefreshToken(
            {
              grantType: 'refreshToken',
              responseType: 'token',
              redirectUri: `${getHostConfig().redirect_uri}`,
              clientId: `${getEnvironmentConfig().client_id}`,
              clientSecret: `${getEnvironmentConfig().client_secret}`,
              refreshToken,
            },
            {
              reject: (error) => {
                // assert(false, 'refresh token failure [useRefreshTokenActionWatcher] - force logout');
                // store.dispatch(actions.authLogout({ reason: 'AUTH_LOGOUT_UNAUTHORIZED' }));
                if ([400, 401, 406, 500].includes(error?.response?.status)) {
                  store.dispatch(actions.invalidateRefreshToken(undefined, { context: 'auth refresh failed' }));
                }
              },
              ...action.meta,
            },
          );
        }
      }

      if (useRefreshTokenAction.payload) {
        const config = resourceStoreTypes.mkQcsSyncResourceConfig({
          resourceName: 'token',
          resourceBaseUrl: '/oauth/token',
          successAction: actions.exchangeRefreshTokenSuccess,
          failureAction: actions.exchangeRefreshTokenFailure,
          axiosConfig: {
            headers: {
              Authorization: null,
            },
          },
          transformResourceWithResponse: (resource, response) => response.data,
        });
        exchangeRefreshTokenInProgress = true;
        yield call(resourceSagaUtils.qcsSyncCreateResource, config, useRefreshTokenAction);
      } else {
        yield put(actions.exchangeRefreshTokenFailure('Valid Refresh Token not found'));
      }
    }
  });
}

export function* exchangeRefreshTokenSuccessActionWatcher() {
  // eslint-disable-next-line require-yield
  yield takeEvery(actions.exchangeRefreshTokenSuccess, function* exchangeRefreshTokenSuccess(action) {
    // Track npsRequested if survey is requested by QCS.
    if (action.payload?.surveyRequested) {
      tracker.track(tracker.events.npsRequested);
    }
    exchangeRefreshTokenInProgress = false;
    exchangeRefreshTokenWaitList.forEach((waitAction) => {
      waitAction.meta?.resolve?.(action.payload);
    });
  });
}

export function* exchangeRefreshTokenFailureActionWatcher() {
  // eslint-disable-next-line require-yield
  yield takeEvery(actions.exchangeRefreshTokenFailure, function* exchangeRefreshTokenFailure(action) {
    exchangeRefreshTokenInProgress = false;
    exchangeRefreshTokenWaitList.forEach((waitAction) => {
      waitAction.meta?.reject?.(action.payload);
    });
  });
}

function SessionStateError(response) {
  this.response = response;
}
function* retrieveOauthToken({ payload: code, meta }) {
  try {
    log.log('Retrieving oAuth Token');
    const requestUrl = `${getEnvironmentConfig().services_url}/oauth/token`;

    const requestBody = {
      clientId: getEnvironmentConfig().client_id,
      clientSecret: getEnvironmentConfig().client_secret,
      grantType: 'authorization_code',
      code,
      redirectUri: getHostConfig().redirect_uri,
    };

    let requestConfig = {
      headers: {
        Authorization: null,
      },
    };

    const sessionState = meta?.props?.sessionState;
    log.debug('local auth session state: ', sessionState);
    if (assert(sessionState, 'no auth session state found #bug-trap')) {
      requestConfig = {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'qcs-session-state': sessionState,
          'qcs-session-state-provided': 'true',
        },
      };
    }

    const response = yield call(httpClientInterface.send, 'POST', requestUrl, requestBody, requestConfig.headers);
    const { data, config } = response;
    if (config && config.headers && config.headers['qcs-session-state']) {
      const sessionStateHeaderValue = config.headers['qcs-session-state'];
      if (sessionStateHeaderValue !== sessionState) {
        throw new SessionStateError({
          data: {
            errors: [
              {
                httpStatus: 401,
                detail: 'QCS Unauthorized Access - Session state mismatch detected',
                code: 'QCS-0401-16',
              },
            ],
          },
          message: 'session state mismatch',
        });
      }
    }
    if (data.accessToken) {
      const authTokenData = new authTypes.AuthTokenData({
        accessToken: data.accessToken,
        accessTokenExpired: data.accessTokenExpired,
        refreshToken: data.refreshToken,
        refreshTokenExpired: data.refreshTokenExpired,
        scope: data.scope,
        surveyRequested: data.surveyRequested,
      });

      yield put(actions.getOAuthTokenSuccess(authTokenData));
      // Track npsRequested if survey is requested by QCS.
      if (data.surveyRequested) {
        tracker.track(tracker.events.npsRequested);
      }
      tracker.resetActiveTimestamp();
    } else {
      yield put(actions.getOAuthTokenFailure(response));
    }
  } catch (error) {
    log.error(`Continued after catching error: ${JSON.stringify(error)}`);

    // {"response":{"data":{"errors":[{"httpStatus":401,"detail":"QCS Unauthorized Access - Session state mismatch detected","code":"QCS-0401-16"}]},"message":"session state mismatch"}}
    try {
      const exceptionData = error.response?.data?.errors?.[0];
      if (exceptionData?.httpStatus === 401 && exceptionData?.code === 'QCS-0401-16') {
        window.location.href = `${getHostConfig().redirect_uri}?session_mismatch=1`;
      }
    } catch (exception) {
      assert(false, exception);
    }

    yield put(actions.getOAuthTokenFailure(error));
  }
}

function* retrieveOauthTokenWatcher() {
  yield takeLatest(actions.getOAuthToken, retrieveOauthToken);
}

function* createDatasetSuccessActionWatcher() {
  yield takeEvery(datasetsActions.createDatasetSuccess, function* createDatasetSuccess(action) {
    const datasetId = yield select(authSelectors.getDatasetId);
    if (action.payload.id === datasetId) {
      yield put(datasetsActions.datasetPoll({ id: action.payload.id }));
    }
  });
}

function* authSelectDatasetActionWatcher() {
  yield takeEvery(actions.authSelectDataset, function* authSelectDataset(action) {
    const { dataset, reload = true, location, userChoice = false } = action.payload;

    if (dataset?.id) {
      if (userChoice) {
        // TODO: replace with prefs v2 once resolved QCS-17004
        yield put(
          preferencesActions.setPreference({
            section: 'shared',
            group: 'user',
            preference: {
              webApp: {
                selectedDatasetId: dataset.id,
              },
            },
          }),
        );
        yield race([
          take(preferencesActions.setPreferenceSuccess),
          take(preferencesActions.setPreferenceFailure),
          delay(3000),
        ]);
        // yield put(preferencesV2Actions.updatePreferenceOverride(preferencesV2Types.mkPreferenceV2({
        //   id: 'selectedDatasetId',
        //   value: dataset.id,
        // })));
        // yield race([
        //   take(preferencesV2Actions.updatePreferenceOverrideSuccess),
        //   take(preferencesV2Actions.updatePreferenceOverrideFailure),
        //   delay(3000),
        // ]);
      }

      const prevAuthSession = yield select(authSelectors.getAuthSession);
      const authSession = prevAuthSession.merge({
        datasetId: dataset?.id ?? null,
        datasetPlatform: dataset?.platform ?? null,
        datasetSubscriptionTier: dataset?.subscriptionTier ?? null,
        datasetCreatedByClientId: dataset?.createdByClientId ?? null,
        datasetCreatedAt: dataset?.createdAt ?? null,
      });
      yield put(
        actions.authUpdated({ authSession, reload, location, prevAuthSession }, { context: 'authSelectDataset' }),
      );
    }

    if (dataset?.shared) {
      tracker.track(tracker.events.sharedDatasetAccessed);
    }
    tracker.resetActiveTimestamp();
  });
}

function* getOAuthTokenFailureWatcher() {
  yield takeEvery(actions.getOAuthTokenFailure, function* getOAuthTokenFailure() {
    yield put(actions.authLogout({ reason: 'AUTH_LOGOUT_UNAUTHORIZED' }, { context: 'getOAuthTokenFailure' }));
  });
}

export default [
  exchangeRefreshTokenActionWatcher,
  exchangeRefreshTokenSuccessActionWatcher,
  exchangeRefreshTokenFailureActionWatcher,
  retrieveOauthTokenWatcher,
  createDatasetSuccessActionWatcher,
  authSelectDatasetActionWatcher,
  getOAuthTokenFailureWatcher,
];
